import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { getSupabase } from '../lib/supabase';
import { useAppConfig } from './AppConfigContext';
import { resolvePaidStatus } from '../lib/paidStatus';

const AuthContext = createContext(null);

/**
 * Supabase-based session context.
 *
 * - Bootstraps the Supabase client from the /api/config payload (via
 *   useAppConfig) so ops can swap creds without a frontend rebuild.
 * - `login*` methods delegate to Supabase's GoTrue SDK:
 *     - loginWithPassword({email, password})
 *     - signUpWithPassword({email, password, referral})
 *     - sendMagicLink({email, referral})
 *     - loginWithGoogle({referral})
 * - On any auth state change we hydrate `/api/auth/me` which merges the
 *   Supabase profiles row with the auth user record (beta flags, storage,
 *   referral counters).
 * - A pending referral code (?ref=...) survives the OAuth round-trip via
 *   sessionStorage and is POSTed to /api/beta/referral/capture the moment
 *   the profile is provisioned.
 */
const REFERRAL_STORAGE_KEY = 'magnidrop-pending-ref';

export function AuthProvider({ children }) {
    const cfg = useAppConfig();
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [initializing, setInitializing] = useState(true);
    const bootedRef = useRef(false);
    const subRef = useRef(null);

    // Pull the effective Supabase client — falls back to env if config is empty.
    const sb = getSupabase({ url: cfg.supabase_url, anonKey: cfg.supabase_anon_key });

    const captureRefIfAny = useCallback(async () => {
        try {
            const ref = sessionStorage.getItem(REFERRAL_STORAGE_KEY);
            if (!ref) return;
            await api.post('/beta/referral/capture', { ref });
            sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
        } catch (_) { /* silent — non-critical */ }
    }, []);

    const hydrateFromApi = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/me');
            setUser(data);
            captureRefIfAny();
        } catch (_) {
            setUser(null);
        }
    }, [captureRefIfAny]);

    // Wire up Supabase session change listener.
    useEffect(() => {
        if (bootedRef.current) return;
        if (!sb) {
            setInitializing(false);
            return;
        }
        bootedRef.current = true;
        (async () => {
            const { data } = await sb.auth.getSession();
            setSession(data?.session || null);
            if (data?.session) {
                await hydrateFromApi();
            } else {
                setUser(null);
            }
            setInitializing(false);
            const sub = sb.auth.onAuthStateChange(async (_evt, newSession) => {
                setSession(newSession);
                if (newSession) {
                    await hydrateFromApi();
                } else {
                    setUser(null);
                }
            });
            subRef.current = sub?.data?.subscription;
        })();
        return () => {
            try { subRef.current?.unsubscribe?.(); } catch (_) { /* noop */ }
        };
    }, [sb, hydrateFromApi]);

    // ---------- Sign-in helpers ----------
    const loginWithPassword = useCallback(async ({ email, password }) => {
        if (!sb) throw new Error('Supabase is not configured yet.');
        const { data, error } = await sb.auth.signInWithPassword({ email, password });
        if (error) throw error;
        return data;
    }, [sb]);

    const signUpWithPassword = useCallback(async ({ email, password, fullName, referral }) => {
        if (!sb) throw new Error('Supabase is not configured yet.');
        if (referral) sessionStorage.setItem(REFERRAL_STORAGE_KEY, referral);
        const { data, error } = await sb.auth.signUp({
            email,
            password,
            options: {
                data: { full_name: fullName || email.split('@')[0] },
                emailRedirectTo: `${window.location.origin}/dashboard`,
            },
        });
        if (error) throw error;
        return data;
    }, [sb]);

    const sendMagicLink = useCallback(async ({ email, referral }) => {
        if (!sb) throw new Error('Supabase is not configured yet.');
        if (referral) sessionStorage.setItem(REFERRAL_STORAGE_KEY, referral);
        const { error } = await sb.auth.signInWithOtp({
            email,
            options: { emailRedirectTo: `${window.location.origin}/dashboard` },
        });
        if (error) throw error;
    }, [sb]);

    const loginWithGoogle = useCallback(async ({ referral } = {}) => {
        if (!sb) throw new Error('Supabase is not configured yet.');
        if (referral) sessionStorage.setItem(REFERRAL_STORAGE_KEY, referral);
        const { error } = await sb.auth.signInWithOAuth({
            provider: 'google',
            options: { redirectTo: `${window.location.origin}/dashboard` },
        });
        if (error) throw error;
    }, [sb]);

    const logout = useCallback(async () => {
        try { if (sb) await sb.auth.signOut(); } catch (_) { /* noop */ }
        setUser(null);
        setSession(null);
    }, [sb]);

    const refresh = hydrateFromApi;

    // Global paid-status resolution — beta phase uses is_paid_beta, public
    // launch (BETA_IS_ACTIVE=false) uses is_paid. Single source of truth.
    const isPaid = resolvePaidStatus(user, cfg.beta_is_active);

    return (
        <AuthContext.Provider value={{
            user, session, initializing, loading: initializing,
            isPaid, betaIsActive: cfg.beta_is_active,
            loginWithPassword, signUpWithPassword, sendMagicLink, loginWithGoogle, logout, refresh,
            supabaseReady: Boolean(sb),
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
